import type { SkillGraphNode } from '../getSkillsLevelByUnit';

const TARGET_IMPACT_RATIO = 0.8;
const UNIT_IMPACT_RATIO = 0.6;
const TOP_OVERALL_LEVEL_RATIO = 0.7; // "top 30%" => at or above 70% of the maximum

/** A skill node as produced by `getSkillsInfoByLevel`: graph metadata plus difficulty/unit info. */
type FoundationSkillNode = SkillGraphNode & { difficulty: number | undefined; unitImpact?: number | undefined };

/**
 * Selects the "foundation" skills from a `getSkillsInfoByLevel` result.
 *
 * Takes the lowest difficulty among the target skills (`easiestTargetDif`). Among the
 * non-target skills *strictly easier* than that, it returns the ones satisfying AT LEAST 2 of 3:
 *   1. `targetSkillImpact` above 80% of the total target-skill count,
 *   2. `unitImpact` at least 60% of `totalUnits`,
 *   3. `overallLevel` within the top 30% of the maximum `overallLevel`.
 *
 * Criterion 2 counts as unmet when `totalUnits` is unknown. Returned nodes keep their original
 * shape and document order.
 */
export const getFoundations = <T extends FoundationSkillNode>(
  skillsInfoByLevel: T[][][],
  totalUnits: number | undefined,
): T[] => {
  const allSkills = skillsInfoByLevel.flat(2);
  const targetSkills = allSkills.filter(skill => skill.isTarget);
  if (!targetSkills.length) return [];

  // Lowest difficulty among the target skills.
  const easiestTargetDif: number = Math.min(...targetSkills.map(skill => skill.difficulty || 0));

  // Global thresholds for the three criteria.
  const targetImpactThreshold = TARGET_IMPACT_RATIO * targetSkills.length;
  const unitImpactThreshold = totalUnits === undefined ? undefined : UNIT_IMPACT_RATIO * totalUnits;
  const maxOverallLevel = allSkills.reduce((max, skill) => Math.max(max, skill.overallLevel), 0);
  const overallLevelThreshold = TOP_OVERALL_LEVEL_RATIO * maxOverallLevel;

  // Candidates: non-target skills strictly easier than the easiest target.
  return allSkills
    .filter(skill => !skill.isTarget && skill.difficulty! < easiestTargetDif)
    .filter(skill => {
      const meetsTargetImpact = skill.targetSkillImpact > targetImpactThreshold;
      const meetsUnitImpact = unitImpactThreshold !== undefined && (skill.unitImpact ?? 0) >= unitImpactThreshold;
      const meetsOverallLevel = skill.overallLevel >= overallLevelThreshold;
      return [meetsTargetImpact, meetsUnitImpact, meetsOverallLevel].filter(Boolean).length >= 2;
    });
};
